import React, { useState, useEffect, useRef } from "react";
import useUnsavedChanges from '../../../hooks/useUnsavedChanges';
import UnsavedChangesDialog from '../../../components/UnsavedChangesDialog';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Box, Container, Paper, Grid, Stack, Typography, Divider,
  FormControl, Select, MenuItem, TextField, Button, Alert, alpha, IconButton,
} from "@mui/material";
import { toast } from 'react-toastify';
import { useTranslation } from "react-i18next";
import DiscountIcon from "@mui/icons-material/Discount";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import SaveIcon from "@mui/icons-material/Save";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CalculateIcon from "@mui/icons-material/Calculate";
import CloseIcon from "@mui/icons-material/Close";
import PreviewIcon from "@mui/icons-material/Preview";

// Services
import { addOffer, getOffer, updateOffer } from "../services/offerService";
import { searchCustomers } from "../../customers/services/customerService";
import { searchProducts } from "../../product/services/productService";
import * as vatService from "../../companysettings/services/vatService";
import * as documentNoteTemplateService from '../../companysettings/services/documentNoteTemplateService';
import { getDefaultTemplateId, getTemplateContent } from '../../../utils/documentNoteTemplateUtils';
import { OFFER_STATUS, normalizeStatus, getStatusLabel } from '../../../constants/offerConstants';

// Hooks
import useStoreInfo from '../../../hooks/useCompanyInfo';

// Shared Document Components
import {
  DocumentHeaderSection,
  DocumentDetailsBar,
  DocumentProductsTable,
  DocumentNotesSection,
} from '../../../components/shared';

// Dialogs
import AddCustomerDialog from "../../customers/components/AddCustomerDialog";
import AddProductDialog from "../../product/components/AddProductDialog";
import InvoicePreviewDialog from "../../../components/InvoicePreviewDialog";

const MAX_CUSTOM_TEXT_LENGTH = 1000;

function formatDate(date) {
  if (!date) return '';
  const d = typeof date === 'string' ? new Date(date) : date;
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
}

function generateOfferNumber() {
  const now = new Date();
  const dd = String(now.getDate()).padStart(2, '0');
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const yy = String(now.getFullYear()).slice(-2);
  const rand = String(Math.floor(1000 + Math.random() * 9000));
  return `OFF-${dd}${mm}${yy}${rand}`;
}

function stripHtml(html) {
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || "";
}

function parseBoolean(value, fallback = true) {
  if (value == null) return fallback;
  if (typeof value === 'string') return value.toLowerCase() === 'true';
  return Boolean(value);
}

function getExclusivePrice(displayedPrice, isTvaIncluded, vatPercentage) {
  const price = parseFloat(displayedPrice) || 0;
  return isTvaIncluded ? price / (1 + vatPercentage / 100) : price;
}



function AddOfferPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { id: offerId } = useParams(); // Get offer ID from URL for edit mode
  const isEditMode = Boolean(offerId);
  const { storeInfo } = useStoreInfo();
  const language = useSelector((state) => state.settings?.language) || i18n.language || "nl";

  // Document state
  const [offerNumber, setOfferNumber] = useState(isEditMode ? '' : generateOfferNumber());
  const [offerDate, setOfferDate] = useState(() => {
    if (isEditMode) return '';
    const today = new Date();
    return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  });
  const [offerStatus, setOfferStatus] = useState(OFFER_STATUS.DRAFT);

  // Customer / Product state
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [offerProducts, setOfferProducts] = useState([]);
  const [isSaved, setIsSaved] = useState(false);
  const blocker = useUnsavedChanges(offerProducts.length > 0 && !isSaved);

  // VAT state
  const [vatPercentage, setVatPercentage] = useState(21);
  const [vatRates, setVatRates] = useState([]);
  const [isTvaIncluded, setIsTvaIncluded] = useState(true);

  // Discount state
  const [discountType, setDiscountType] = useState("percentage");
  const [discountValue, setDiscountValue] = useState(0);

  // Notes state
  const [customText, setCustomText] = useState(
    () => t('offer_validity_note') || 'This quotation is valid for 30 days from the date of issue'
  );
  const [noteTemplates, setNoteTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');

  // UI state
  const [loading, setLoading] = useState(false);
  const [openCustomerDialog, setOpenCustomerDialog] = useState(false);
  const [openProductDialog, setOpenProductDialog] = useState(false);
  const [productSearchText, setProductSearchText] = useState('');
  const [pendingProductForTable, setPendingProductForTable] = useState(null);
  const [customerError, setCustomerError] = useState('');
  
  // Preview state
  const [templateHtml, setTemplateHtml] = useState('');
  const [openPreview, setOpenPreview] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');

  // Fetch VAT rates
  useEffect(() => {
    vatService.getVatRates().then(data => {
      setVatRates(data?.payload || data || []);
    }).catch(() => {});
  }, []);

  // Load offer template
  useEffect(() => {
    let file = "offer-template-nl.html";
    if (language === "fr") file = "offer-template-fr.html";
    else if (language === "en") file = "offer-template-en.html";
    
    fetch(`/offertemplates/${file}`)
      .then(res => res.text())
      .then(html => setTemplateHtml(html))
      .catch(() => toast.error(t('failed_to_load_offer_template') || 'Failed to load offer template'));
  }, [language]);

  // Fetch note templates
  useEffect(() => {
    if (noteTemplates.length > 0) return;
    documentNoteTemplateService.getTemplatesByType('Offer').then(data => {
      const allTemplates = data?.payload || data || [];
      const filtered = allTemplates.filter(tmpl => tmpl.DocumentType === 4 || tmpl.DocumentType === 99);
      setNoteTemplates(filtered);
      const defaultId = getDefaultTemplateId(filtered);
      if (defaultId) {
        setSelectedTemplate(defaultId);
        const templateContent = getTemplateContent(filtered, defaultId);
        if (!isEditMode) {
          setCustomText(templateContent || '');
        }
      }
    }).catch(() => {});
  }, [noteTemplates.length, isEditMode, t]);

  // Pre-fill customer from navigation
  useEffect(() => {
    if (location.state?.prefilledCustomer) {
      setSelectedCustomer(location.state.prefilledCustomer);
      if (window.history.state) {
        window.history.replaceState({ ...window.history.state, usr: null }, '');
      }
    }
  }, [location.state]);

  // Fetch offer data for edit mode
  useEffect(() => {
    if (!isEditMode || !offerId) return;
    
    const fetchOfferData = async () => {
      try {
        setLoading(true);
        const response = await getOffer(offerId);
        const offerData = response?.payload || response;
        
        if (!offerData) {
          toast.error(t('failed_to_load_offer_data') || 'Failed to load offer data');
          navigate('/sales/offers');
          return;
        }

        // Map offer data to state
        setOfferNumber(offerData.OfferNumber || '');
        if (offerData.OfferDate) {
          const date = new Date(offerData.OfferDate);
          setOfferDate(`${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`);
        }
        setOfferStatus(normalizeStatus(offerData.Status));
        setVatPercentage(offerData.VATPercentage || 21);
        const loadedIsTvaIncluded = parseBoolean(offerData.IsTvaIncluded);
        setIsTvaIncluded(loadedIsTvaIncluded);
        setCustomText(offerData.CustomText || offerData.Notes || '');
        
        // Map customer data
        if (offerData.Customer) {
          setSelectedCustomer({
            Id: offerData.Customer.Id || offerData.CustomerId,
            Name: offerData.Customer.CompanyName || offerData.Customer.Name,
            CompanyName: offerData.Customer.CompanyName,
            VATNumber: offerData.Customer.VATNumber,
            Street: offerData.Customer.Street,
            Box: offerData.Customer.Box,
            PostCode: offerData.Customer.PostCode,
            City: offerData.Customer.City,
            Country: offerData.Customer.Country,
            value: offerData.Customer.Id || offerData.CustomerId,
            label: offerData.Customer.CompanyName || offerData.Customer.Name,
          });
        }
        
        // Map discount
        if (offerData.Discount > 0) {
          const totalExclWithoutDiscount = offerData.LineItems?.reduce((sum, item) => {
            return sum + (item.Quantity * item.UnitPrice);
          }, 0) || 0;
          
          // Try to determine if it was a percentage discount
          const potentialPercentage = (offerData.Discount / totalExclWithoutDiscount) * 100;
          if (Number.isInteger(potentialPercentage) && potentialPercentage > 0 && potentialPercentage <= 100) {
            setDiscountType('percentage');
            setDiscountValue(potentialPercentage);
          } else {
            setDiscountType('fixed');
            setDiscountValue(offerData.Discount);
          }
        }
        
        // Map line items to products
        if (offerData.LineItems && offerData.LineItems.length > 0) {
          const mappedProducts = offerData.LineItems.map((item, index) => {
            const quantity = item.Quantity || 1;
            const vatPercent = item.VATPercent || offerData.VATPercentage || 21;
            const storedSalePrice = item.SalePrice ?? item.UnitPriceExclTVA ?? item.UnitPrice ?? 0;
            const displayedUnitPrice = loadedIsTvaIncluded
              ? (item.UnitPriceInclTVA ?? storedSalePrice * (1 + vatPercent / 100))
              : storedSalePrice;
            const salePrice = getExclusivePrice(displayedUnitPrice, loadedIsTvaIncluded, vatPercent);
            const totalExcl = item.TotalExcl || (quantity * salePrice);
            const vatAmount = totalExcl * (vatPercent / 100);
            const totalIncl = totalExcl + vatAmount;
            
            return {
              ...item.Product,
              ProductId: item.ProductId,
              LineItemId: item.Id ?? item.LineItemId,
              SalePrice: salePrice,
              Name: item.Product?.Name || item.ProductName || `${t('product') || 'Product'} ${item.ProductId}`,
              Quantity: quantity,
              Price: displayedUnitPrice,
              UnitPrice: displayedUnitPrice,
              VAT: vatPercent,
              TotalExcl: totalExcl,
              VATAmount: vatAmount,
              TotalIncl: totalIncl,
              _rowKey: Date.now() + index,
              Id: item.Product?.Id || item.ProductId,
              SKU: item.Product?.SKU,
              SerialNumber: item.Product?.SerialNumber,
            };
          });
          setOfferProducts(mappedProducts);
        }
        
      } catch (error) {
        console.error('Error fetching offer:', error);
        toast.error(t('failed_to_load_offer_data') || 'Failed to load offer data');
        navigate('/sales/offers');
      } finally {
        setLoading(false);
      }
    };
    
    fetchOfferData();
  }, [isEditMode, offerId, navigate]);

  // Recalculate line totals when the VAT rate or price type changes.
  useEffect(() => {
    if (offerProducts.length > 0) {
      setOfferProducts(prev => prev.map(p => {
        const displayedPrice = parseFloat(p.Price ?? p.UnitPrice ?? p.SalePrice ?? 0);
        const salePrice = getExclusivePrice(displayedPrice, isTvaIncluded, vatPercentage);
        let totalExcl, vatAmount, totalIncl;
        const qty = p.Quantity;

        totalExcl = Math.round(salePrice * qty * 100) / 100;
        vatAmount = totalExcl * (vatPercentage / 100);
        totalIncl = totalExcl + vatAmount;

        return {
          ...p,
          SalePrice: salePrice,
          Price: displayedPrice,
          UnitPrice: displayedPrice,
          VAT: vatPercentage,
          TotalExcl: totalExcl.toFixed(2),
          VATAmount: vatAmount.toFixed(2),
          TotalIncl: totalIncl.toFixed(2),
        };
      }));
    }
  }, [isTvaIncluded, vatPercentage]);

  // Computed totals
  const totalExcl = offerProducts.reduce((sum, p) => sum + Number(p.TotalExcl || 0), 0);
  let discountAmount = 0;
  if (discountType === "percentage") {
    discountAmount = (totalExcl * (parseFloat(discountValue) || 0)) / 100;
  } else {
    discountAmount = parseFloat(discountValue) || 0;
  }
  discountAmount = Math.max(0, Math.min(totalExcl, discountAmount));
  const amountAfterDiscount = totalExcl - discountAmount;
  const totalVAT = isTvaIncluded
    ? offerProducts.reduce((sum, product) => sum + Number(product.VATAmount || 0), 0) *
      (amountAfterDiscount / totalExcl || 0)
    : (amountAfterDiscount * vatPercentage) / 100;
  const offerTotal = amountAfterDiscount + totalVAT;

  // Product handlers (compatible with DocumentProductsTable)
  const handleAddProduct = (product, qty, price) => {
    if (!product || !qty || !price) return;
    const quantity = parseInt(qty) || 1;
    const displayedPrice = parseFloat(price) || 0;
    const salePrice = getExclusivePrice(displayedPrice, isTvaIncluded, vatPercentage);
    const productId = product.value ?? product.Id ?? product.ProductId;

    setOfferProducts(prev => {
      const existingIndex = prev.findIndex(item => {
        const existingProductId = item.ProductId ?? item.Id;
        return existingProductId != null &&
          String(existingProductId) === String(productId) &&
          Math.abs(Number(item.SalePrice ?? item.Price ?? item.UnitPrice ?? 0) - salePrice) < 0.000001;
      });

      if (existingIndex === -1) {
        const totalExcl = Math.round(salePrice * quantity * 100) / 100;
        const vatAmt = totalExcl * (vatPercentage / 100);
        const totalIncl = totalExcl + vatAmt;
        return [...prev, {
          ...product,
          ProductId: productId,
          SalePrice: salePrice,
          Name: product.Name || product.label,
          Quantity: quantity,
          Price: displayedPrice,
          UnitPrice: displayedPrice,
          VAT: vatPercentage,
          TotalExcl: totalExcl.toFixed(2),
          VATAmount: vatAmt.toFixed(2),
          TotalIncl: totalIncl.toFixed(2),
          _rowKey: Date.now() + Math.random(),
        }];
      }

      return prev.map((item, index) => {
        if (index !== existingIndex) return item;
        const newQuantity = Number(item.Quantity || 0) + quantity;
        const totalExcl = Math.round(salePrice * newQuantity * 100) / 100;
        const vatAmt = totalExcl * (vatPercentage / 100);
        const totalIncl = totalExcl + vatAmt;
        return {
          ...item,
          Quantity: newQuantity,
          TotalExcl: totalExcl.toFixed(2),
          VATAmount: vatAmt.toFixed(2),
          TotalIncl: totalIncl.toFixed(2),
        };
      });
    });
  };

  const handleRemoveProduct = async (product) => {
    const remainingProducts = offerProducts.filter(p => p._rowKey !== product._rowKey);
    const lineItemId = product.LineItemId;

    if (isEditMode && remainingProducts.length === 0) {
      toast.warning('You cannot delete the last line item. Delete the offer instead.');
      return;
    }

    if (!isEditMode || lineItemId == null) {
      setOfferProducts(remainingProducts);
      return;
    }

    try {
      const remainingTotalExcl = remainingProducts.reduce(
        (sum, item) => sum + Number(item.TotalExcl || 0),
        0
      );
      const remainingDiscount = discountType === 'percentage'
        ? (remainingTotalExcl * (parseFloat(discountValue) || 0)) / 100
        : parseFloat(discountValue) || 0;
      const remainingAfterDiscount = remainingTotalExcl - remainingDiscount;
      const remainingLineVat = remainingProducts.reduce(
        (sum, item) => sum + Number(item.VATAmount || 0),
        0
      );
      const remainingVat = isTvaIncluded
        ? remainingLineVat * (remainingAfterDiscount / remainingTotalExcl || 0)
        : (remainingAfterDiscount * vatPercentage) / 100;

      const response = await updateOffer(offerId, {
        Id: Number(offerId),
        CustomerId: selectedCustomer.Id || selectedCustomer.value,
        OfferNumber: offerNumber,
        OfferDate: new Date(offerDate).toISOString(),
        OfferTotal: remainingAfterDiscount + remainingVat,
        CustomText: customText,
        Notes: customText,
        Discount: remainingDiscount,
        VATPercentage: vatPercentage,
        IsTvaIncluded: isTvaIncluded,
        Status: offerStatus ?? OFFER_STATUS.DRAFT,
        LineItems: remainingProducts.map(item => ({
          Id: item.LineItemId ?? 0,
          ProductId: item.ProductId || item.value || item.Id,
          Quantity: item.Quantity,
          UnitPrice: item.SalePrice ?? item.Price ?? item.UnitPrice,
          SalePrice: item.SalePrice ?? item.Price ?? item.UnitPrice,
          UnitPriceExclTVA: item.SalePrice ?? item.Price ?? item.UnitPrice,
          UnitPriceInclTVA: (item.SalePrice ?? item.Price ?? item.UnitPrice) * (1 + vatPercentage / 100),
          VATPercent: item.VAT || vatPercentage,
          VATPercentage: item.VAT || vatPercentage,
        })),
      });

      if (response?.status === 'Failure' || response?.errorCode) {
        throw new Error(response?.message || 'Failed to delete offer line item.');
      }

      setOfferProducts(remainingProducts);
      toast.success(t('offer_updated_successfully') || 'Offer updated successfully!');
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
        t('failed_to_save_offer_try_again', { action: t('offer_action_update') }) ||
        'Failed to delete offer line item.'
      );
    }
  };

  const handleQuantityChange = (product, newQuantity) => {
    if (newQuantity < 0) return;
    setOfferProducts(prev => prev.map(p => {
      if (p._rowKey !== product._rowKey) return p;
      const qty = parseInt(newQuantity) || 0;
      const displayedPrice = parseFloat(p.Price ?? p.UnitPrice ?? p.SalePrice ?? 0);
      const salePrice = getExclusivePrice(displayedPrice, isTvaIncluded, vatPercentage);
      
      let totalExcl, vatAmt, totalIncl;
      totalExcl = Math.round(salePrice * qty * 100) / 100;
      vatAmt = totalExcl * (vatPercentage / 100);
      totalIncl = totalExcl + vatAmt;
      
      return { 
        ...p, 
        Quantity: qty, 
        SalePrice: salePrice,
        Price: displayedPrice,
        UnitPrice: displayedPrice,
        TotalExcl: totalExcl.toFixed(2), 
        VATAmount: vatAmt.toFixed(2), 
        TotalIncl: totalIncl.toFixed(2) 
      };
    }));
  };

  const handlePriceChange = (product, newPrice) => {
    setOfferProducts(prev => prev.map(p => {
      if (p._rowKey !== product._rowKey) return p;
      const displayedPrice = parseFloat(newPrice) || 0;
      const salePrice = getExclusivePrice(displayedPrice, isTvaIncluded, vatPercentage);
      
      const totalExcl = Math.round(salePrice * p.Quantity * 100) / 100;
      const vatAmt = totalExcl * (vatPercentage / 100);
      const totalIncl = totalExcl + vatAmt;
      
      return { 
        ...p, 
        SalePrice: salePrice,
        Price: displayedPrice,
        UnitPrice: displayedPrice,
        TotalExcl: totalExcl.toFixed(2), 
        VATAmount: vatAmt.toFixed(2), 
        TotalIncl: totalIncl.toFixed(2) 
      };
    }));
  };

  const handlePriceBlur = (product, finalPrice) => {
    const displayedPrice = parseFloat(finalPrice) || 0;
    setOfferProducts(prev => prev.map(p => {
      if (p._rowKey !== product._rowKey) return p;
      
      const salePrice = getExclusivePrice(displayedPrice, isTvaIncluded, vatPercentage);
      const totalExcl = Math.round(salePrice * p.Quantity * 100) / 100;
      const vatAmt = totalExcl * (vatPercentage / 100);
      const totalIncl = totalExcl + vatAmt;
      
      return { 
        ...p, 
        SalePrice: salePrice,
        Price: displayedPrice,
        UnitPrice: displayedPrice,
        TotalExcl: totalExcl.toFixed(2), 
        VATAmount: vatAmt.toFixed(2), 
        TotalIncl: totalIncl.toFixed(2) 
      };
    }));
  };

  const customerDebounceRef = useRef();
  
  const loadCustomerOptions = (inputValue, callback) => {
    if (customerDebounceRef.current) clearTimeout(customerDebounceRef.current);
    customerDebounceRef.current = setTimeout(async () => {
      if (!inputValue || inputValue.trim().length < 2) return callback([]);
      try {
        const customers = await searchCustomers(inputValue);
        callback(Array.isArray(customers) ? customers : []);
      } catch {
        callback([]);
      }
    }, 400);
  };

  const productDebounceRef = useRef();
  const loadProductOptions = (inputValue, callback) => {
    if (productDebounceRef.current) clearTimeout(productDebounceRef.current);
    productDebounceRef.current = setTimeout(async () => {
      if (!inputValue || inputValue.length < 2) return callback([]);
      try {
        const paramsObj = /^\d+$/.test(inputValue) ? { skuBarCode: inputValue } : { name: inputValue };
        const data = await searchProducts(paramsObj);
        const productOptions = (data?.payload || []).map(p => ({
          value: p.Id,
          label: `${p.Name}${p.SKU ? ' - ' + p.SKU : ''}`,
          ...p,
        }));
        if (inputValue.length >= 3) {
          productOptions.push({
            value: '__add_new__',
            label: `➕ ${t('add_new_product') || 'Add new product'} "${inputValue}"`,
            isCustomOption: true,
            searchText: inputValue,
          });
        }
        callback(productOptions);
      } catch {
        callback([]);
      }
    }, 350);
  };

  const handleSubmit = async () => {
    if (!selectedCustomer) {
      setCustomerError(t('please_select_customer') || 'Please select a customer.');
      return;
    }
    if (offerProducts.length === 0) {
      toast.error(t('please_add_product') || 'Please add at least one product.');
      return;
    }
    setCustomerError('');
    setLoading(true);

    const offerData = {
      ...(isEditMode ? { Id: Number(offerId) } : {}),
      CustomerId: selectedCustomer.Id || selectedCustomer.value,
      OfferNumber: offerNumber,
      OfferDate: new Date(offerDate).toISOString(),
      OfferTotal: offerTotal,
      CustomText: customText,
      Notes: customText,
      Discount: discountAmount,
      VATPercentage: vatPercentage,
      IsTvaIncluded: isTvaIncluded,
      Status: offerStatus ?? OFFER_STATUS.DRAFT,
      LineItems: offerProducts.map(p => ({
        Id: p.LineItemId ?? 0,
        ProductId: p.ProductId || p.value || p.Id,
        Quantity: p.Quantity,
        UnitPrice: p.SalePrice ?? p.Price ?? p.UnitPrice,
        SalePrice: p.SalePrice ?? p.Price ?? p.UnitPrice,
        UnitPriceExclTVA: p.SalePrice ?? p.Price ?? p.UnitPrice,
        UnitPriceInclTVA: (p.SalePrice ?? p.Price ?? p.UnitPrice) * (1 + vatPercentage / 100),
        VATPercent: p.VAT || vatPercentage,
        VATPercentage: p.VAT || vatPercentage,
      })),
    };

    try {
      const response = isEditMode 
        ? await updateOffer(offerId, offerData)
        : await addOffer(offerData);
        
      if (response?.status === "Failure" || response?.errorCode || response?.statusCode === 401) {
        if (response?.errorCode === 12 || response?.message === "Unauthorized") {
          toast.error(t('unauthorized_login_again') || 'Unauthorized: Please log in again.');
        } else {
          toast.error(response?.message || t('failed_to_save_offer', { action: t(isEditMode ? 'offer_action_update' : 'offer_action_create') }));
        }
        return;
      }
      if (response?.status === "Success" || response?.payload || response?.Id) {
        toast.success(isEditMode 
          ? (t('offer_updated_successfully') || "Offer updated successfully!") 
          : (t('offer_created_successfully') || "Offer created successfully!")
        );
        const resultOfferId = response?.payload?.Id || response?.Id || offerId;
        setIsSaved(true);
        blocker.bypass();
        navigate(resultOfferId ? `/sales/offers/${resultOfferId}` : '/sales/offers');
      } else {
        toast.error(t('failed_to_save_offer_try_again', { action: t(isEditMode ? 'offer_action_update' : 'offer_action_create') }));
      }
    } catch (err) {
      if (err?.response?.status === 401 || err?.response?.data?.errorCode === 12) {
        toast.error(t('unauthorized_login_again') || 'Unauthorized: Please log in again.');
      } else if (err?.response?.data?.message) {
        toast.error(`Error: ${err.response.data.message}`);
      } else if (err.message) {
        toast.error(err.message);
      } else {
        toast.error(t('failed_to_save_offer_connection', { action: t(isEditMode ? 'offer_action_update' : 'offer_action_create') }));
      }
    } finally {
      setLoading(false);
    }
  };

  const getFilledTemplate = () => {
    if (!templateHtml) return `<p>${t('template_not_loaded') || 'Template not loaded'}</p>`;
    
    const tq = offerProducts.reduce((s, p) => s + Number(p.Quantity), 0);
    const discountRatio = totalExcl > 0
      ? Math.max(0, Math.min(1, discountAmount / totalExcl))
      : 0;
    const DR = discountAmount > 0 
      ? `<tr class="discount-row"><td colspan="5" style="text-align:right;font-style:italic;">${discountType === "percentage" ? `${discountValue}%` : `€${discountAmount.toFixed(2)}`} Discount:</td><td class="text-right">- €${discountAmount.toFixed(2)}</td></tr>` 
      : "";
    
    const PR = offerProducts.map((p) => {
      const n = p.SerialNumber ? `${p.Name} (SN: ${p.SerialNumber})` : p.Name;
      const lineTotalExcl = parseFloat(p.TotalExcl || 0);
      const lineTotalAfterDiscount = lineTotalExcl * (1 - discountRatio);
      const lineVatAfterDiscount = lineTotalAfterDiscount * (vatPercentage / 100);
      const lineTotalDisplayed = lineTotalAfterDiscount + lineVatAfterDiscount;
      return `<tr><td>${n}</td><td class="text-center">${p.Quantity}</td><td class="text-right">€${parseFloat(p.Price || p.UnitPrice || 0).toFixed(2)}</td><td class="text-right">€${lineTotalAfterDiscount.toFixed(2)}</td><td class="text-right">€${lineVatAfterDiscount.toFixed(2)}</td><td class="text-right">€${lineTotalDisplayed.toFixed(2)}</td></tr>`;
    }).join("");

    const VN = customText && customText.trim()
      ? `<div class="validity-notice"><p style="white-space: pre-wrap;">${customText}</p></div>`
      : "";

    const iban = storeInfo?.iban || "";
    let html = templateHtml
      .replace(/{{CompanyName}}/g, storeInfo?.name || "N/A")
      .replace(/{{CompanyVAT}}/g, storeInfo?.vat || "N/A")
      .replace(/{{CompanyIBAN}}/g, iban || "N/A")
      .replace(/{{CompanyStreetAddress}}/g, storeInfo?.address || "")
      .replace(/{{CompanyCityCountry}}/g, [storeInfo?.postCode, storeInfo?.city, storeInfo?.country].filter(Boolean).join(" ") || "")
      .replace(/{{CustomerName}}/g, selectedCustomer?.CompanyName || selectedCustomer?.Name || "N/A")
      .replace(/{{CustomerVAT}}/g, selectedCustomer?.VATNumber || "N/A")
      .replace(/{{CustomerStreetAddress}}/g, `${selectedCustomer?.Street || ""} ${selectedCustomer?.Box || ""}`.trim() || "N/A")
      .replace(/{{CustomerCityCountry}}/g, [selectedCustomer?.PostCode, selectedCustomer?.City, selectedCustomer?.Country].filter(Boolean).join(" ") || "N/A")
      .replace(/{{OfferNumber}}/g, offerNumber)
      .replace(/{{OfferDate}}/g, formatDate(offerDate))
      .replace(/{{{ProductRows}}}/g, PR)
      .replace(/{{TotalQuantity}}/g, String(tq))
      .replace(/{{{DiscountRow}}}/g, DR)
      .replace(/{{TotalExcl}}/g, totalExcl.toFixed(2))
      .replace(/{{TotalVAT}}/g, totalVAT.toFixed(2))
      .replace(/{{VAT}}/g, String(vatPercentage))
      .replace(/{{GrandTotal}}/g, offerTotal.toFixed(2))
      .replace(/{{{ValidityNotice}}}/g, VN)
      .replace(/{{{NotesSection}}}/g, '');
    
    // Hide VAT row if VAT percentage is 0
    if (vatPercentage === 0) {
      html = html.replace(/<div[^>]*>\s*<span>VAT[^<]*<\/span>\s*<span>[^<]*<\/span>\s*<\/div>/gi, '');
    }
    
    // Add has-content class to bank-details if IBAN exists
    if (iban && iban !== "N/A") {
      html = html.replace('<div class="bank-details">', '<div class="bank-details has-content">');
    }
    
    return html;
  };

  const handlePreviewOffer = () => {
    setPreviewHtml(getFilledTemplate());
    setOpenPreview(true);
  };

  const statusRibbonColor = '#9e9e9e';

  return (
    <Box sx={{
      background: 'linear-gradient(160deg, #ede7f6 0%, #e8eaf6 50%, #f3e5f5 100%)',
      minHeight: '100vh',
      py: 3,
    }}>
      <Container
        maxWidth={false}
        sx={{
          px: { xs: 2, sm: 3, md: 4, lg: 6 },
          maxWidth: '1600px',
          margin: '0 auto',
        }}
      >
        <Paper
          elevation={3}
          sx={{
            background: '#fafaf9',
            boxShadow: '0 0 24px rgba(103, 126, 234, 0.10)',
            borderRadius: '8px',
            overflow: 'visible',
            maxWidth: '100%',
            position: 'relative',
            borderTop: '4px solid #667eea',
          }}
        >
          {/* Status Ribbon */}
          <Box
            sx={{
              position: 'absolute', top: 0, left: 0,
              bgcolor: statusRibbonColor,
              color: 'white', px: 3, py: 1,
              fontWeight: 700, fontSize: '0.875rem',
              textTransform: 'uppercase', letterSpacing: '1px',
              borderTopLeftRadius: '8px', borderBottomRightRadius: '8px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)', zIndex: 10,
            }}
          >
            {getStatusLabel(offerStatus)}
          </Box>

          <Box sx={{ p: { xs: 3, sm: 4, md: 5, lg: 6 }, pt: { xs: 6, sm: 6, md: 7, lg: 8 } }}>

            {/* Company + Customer */}
            <DocumentHeaderSection
              storeInfo={storeInfo}
              selectedCustomer={selectedCustomer}
              onCustomerChange={(c) => { setSelectedCustomer(c); setCustomerError(''); }}
              onCustomerClear={() => setSelectedCustomer(null)}
              onAddCustomerClick={() => setOpenCustomerDialog(true)}
              loadCustomerOptions={loadCustomerOptions}
              documentLabel={t('offer') || 'Offer'}
              accentColor="#667eea"
            />

            {customerError && (
              <Alert severity="error" sx={{ mb: 3 }}>{customerError}</Alert>
            )}

            {/* Details bar */}
            <DocumentDetailsBar
              documentNumber={offerNumber}
              documentNumberLabel={t('offer_number') || 'Offer #'}
              documentDate={offerDate}
              onDateChange={(d) => setOfferDate(d instanceof Date ? d.toISOString().split('T')[0] : d)}
              vatPercentage={vatPercentage}
              onVatChange={setVatPercentage}
              vatRates={vatRates}
              itemsCount={offerProducts.length}
              isTvaIncluded={isTvaIncluded}
              onTvaToggle={(checked) => setIsTvaIncluded(Boolean(checked))}
              showPriceTypeToggle={true}
              dateLabel={t('offer_date') || 'Offer Date'}
            />

            {/* Products Table */}
            <DocumentProductsTable
              products={offerProducts}
              onProductAdd={handleAddProduct}
              onProductRemove={handleRemoveProduct}
              onQuantityChange={handleQuantityChange}
              onPriceChange={handlePriceChange}
              onPriceBlur={handlePriceBlur}
              onAddProductClick={() => setOpenProductDialog(true)}
              loadProductOptions={loadProductOptions}
              vatPercentage={vatPercentage}
              isTvaIncluded={isTvaIncluded}
              externalPendingProduct={pendingProductForTable}
              onExternalPendingConsumed={() => setPendingProductForTable(null)}
              onCustomProductOptionSelect={(type, searchText) => {
                if (type === 'add_new') {
                  setProductSearchText(searchText);
                  setOpenProductDialog(true);
                }
              }}
            />

            {/* Bottom: Discount + Status + Notes (left) | Totals + Actions (right) */}
            <Grid container spacing={3} sx={{ mt: 1 }}>

              {/* LEFT */}
              <Grid item xs={12} md={7}>
                <Stack spacing={3}>

                  {/* Discount */}
                  <Box sx={{
                    p: 3, background: '#f8f9fa', borderRadius: '8px',
                    border: '1px solid #e0e0e0', borderLeft: '4px solid #ff9800',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                  }}>
                    <Typography variant="subtitle2" fontWeight="700" color="#2c3e50" sx={{ textTransform: 'uppercase', mb: 1.5, fontSize: '0.85rem' }}>
                      <DiscountIcon sx={{ fontSize: 18, mr: 0.5, verticalAlign: 'middle' }} />
                      {t("discount")}
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <FormControl fullWidth size="small">
                          <Select
                            value={discountType}
                            onChange={(e) => setDiscountType(e.target.value)}
                            disabled={offerProducts.length === 0}
                            sx={{ bgcolor: 'white' }}
                          >
                            <MenuItem value="percentage">{t("percentage")}</MenuItem>
                            <MenuItem value="fixed">{t("fixed_amount")}</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          value={discountValue}
                          onChange={(e) => {
                            const v = e.target.value;
                            if (v === '' || /^\d*\.?\d{0,2}$/.test(v)) setDiscountValue(v);
                          }}
                          onBlur={(e) => {
                            const n = parseFloat(e.target.value);
                            setDiscountValue(isNaN(n) || n < 0 ? 0 : parseFloat(n.toFixed(2)));
                          }}
                          size="small" fullWidth
                          disabled={offerProducts.length === 0}
                          inputProps={{ min: 0, step: 0.01 }}
                          sx={{ bgcolor: 'white' }}
                          InputProps={{
                            endAdornment: (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Typography variant="body2">{discountType === "percentage" ? "%" : "€"}</Typography>
                                {discountValue > 0 && (
                                  <IconButton size="small" onClick={() => setDiscountValue(0)} sx={{ p: 0.5 }}>
                                    <CloseIcon fontSize="small" />
                                  </IconButton>
                                )}
                              </Box>
                            ),
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Box>

                  {/* Notes */}
                  <DocumentNotesSection
                    customText={customText}
                    onCustomTextChange={(text) => {
                      if (text.length <= MAX_CUSTOM_TEXT_LENGTH) setCustomText(text);
                    }}
                    noteTemplates={noteTemplates}
                    selectedTemplate={selectedTemplate}
                    onTemplateSelect={(id) => {
                      setSelectedTemplate(id);
                      if (id) {
                        const tmpl = noteTemplates.find((tmplItem) => tmplItem.Id === id);
                        if (tmpl) {
                          const templateContent = stripHtml(tmpl.NoteContent).slice(0, MAX_CUSTOM_TEXT_LENGTH);
                          setCustomText(templateContent);
                        }
                      } else {
                        setCustomText('');
                      }
                    }}
                    maxLength={MAX_CUSTOM_TEXT_LENGTH}
                    stripHtml={stripHtml}
                  />
                </Stack>
              </Grid>

              {/* RIGHT: Totals + Actions */}
              <Grid item xs={12} md={5}>
                <Stack spacing={3}>

                  {/* Totals Summary */}
                  <Box sx={{
                    border: '1px solid #e0e0e0', borderRadius: '8px',
                    overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                  }}>
                    <Box sx={{ p: 2, background: '#f8f9fa', borderBottom: '1px solid #e0e0e0' }}>
                      <Typography variant="subtitle1" fontWeight="700" color="#2c3e50" sx={{ textTransform: 'uppercase', fontSize: '0.9rem' }}>
                        <CalculateIcon sx={{ fontSize: 18, mr: 0.5, verticalAlign: 'middle' }} />
                        {t("summary")}
                      </Typography>
                    </Box>
                    <Box sx={{ p: 2 }}>
                      <Stack spacing={1.5}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="body2" color="#555">{t("subtotal")}</Typography>
                          <Typography variant="body1" fontWeight={600}>€{totalExcl.toFixed(2)}</Typography>
                        </Box>

                        {discountValue > 0 && (
                          <>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1, bgcolor: '#fff3e0', borderRadius: '4px' }}>
                              <Typography variant="body2" color="#ff9800" fontWeight={600}>
                                {t("discount")}{discountType === "percentage" ? ` (${discountValue}%)` : ""}
                              </Typography>
                              <Typography variant="body1" fontWeight={600} color="#ff9800">-€{discountAmount.toFixed(2)}</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Typography variant="body2" color="#555" fontWeight={600}>{t("subtotal_after_discount")}</Typography>
                              <Typography variant="body1" fontWeight={600}>€{amountAfterDiscount.toFixed(2)}</Typography>
                            </Box>
                          </>
                        )}

                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1, bgcolor: '#e8f4f8', borderRadius: '4px' }}>
                          <Typography variant="body2" color="#0066cc" fontWeight={600}>{t("vat")} ({vatPercentage}%)</Typography>
                          <Typography variant="body1" fontWeight={600} color="#0066cc">€{totalVAT.toFixed(2)}</Typography>
                        </Box>

                        <Divider sx={{ my: 1 }} />

                        <Box sx={{
                          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                          p: 2, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          borderRadius: '6px',
                        }}>
                          <Typography variant="h6" fontWeight="700" color="white">{t("total")}</Typography>
                          <Typography variant="h5" fontWeight="700" color="white">€{offerTotal.toFixed(2)}</Typography>
                        </Box>
                      </Stack>
                    </Box>
                  </Box>

                  {/* Action Buttons */}
                  <Stack spacing={0} sx={{ gap: 2.5 }}>
                    <Button
                      variant="contained"
                      fullWidth
                      size="large"
                      startIcon={<SaveIcon />}
                      onClick={handleSubmit}
                      disabled={loading || offerProducts.length === 0 || !selectedCustomer}
                      sx={{
                        order: 2,
                        py: 1.5, borderRadius: '8px',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        fontSize: '1rem', textTransform: 'none', fontWeight: 700,
                        boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #5a6fd6 0%, #6a4190 100%)',
                          boxShadow: '0 6px 20px rgba(102, 126, 234, 0.5)',
                          transform: 'translateY(-1px)',
                        },
                        '&.Mui-disabled': { background: '#bbb', boxShadow: 'none' },
                      }}
                    >
                      {loading 
                        ? (t('saving') || 'Saving...') 
                        : isEditMode 
                          ? (t('update_offer') || 'Update Offer')
                          : (t("create_in_draft") || 'Create In Draft')
                      }
                    </Button>
                    <Button
                      variant="outlined"
                      fullWidth
                      startIcon={<PreviewIcon />}
                      onClick={handlePreviewOffer}
                      disabled={!selectedCustomer || offerProducts.length === 0}
                      sx={{
                        order: 1,
                        borderRadius: '8px', py: 1.5,
                        borderColor: '#667eea', color: '#667eea', fontWeight: 600,
                        '&:hover': { borderColor: '#764ba2', bgcolor: alpha('#667eea', 0.05) },
                      }}
                    >
                      {t("preview_offer") || "Preview Offer"}
                    </Button>
                    <Button
                      variant="outlined"
                      fullWidth
                      startIcon={<ArrowBackIcon />}
                      onClick={() => navigate('/sales/offers')}
                      sx={{
                        order: 3,
                        borderRadius: '8px', borderColor: '#667eea', color: '#667eea',
                        '&:hover': { borderColor: '#764ba2', bgcolor: alpha('#667eea', 0.05) },
                      }}
                    >
                      {t("back") || "Back"}
                    </Button>
                  </Stack>
                </Stack>
              </Grid>
            </Grid>
          </Box>
        </Paper>
      </Container>

      {/* Dialogs */}
      <AddCustomerDialog
        open={openCustomerDialog}
        onClose={() => setOpenCustomerDialog(false)}
        onCustomerAdded={(customer) => {
          setSelectedCustomer(customer);
          setOpenCustomerDialog(false);
        }}
      />
      <AddProductDialog
        open={openProductDialog}
        onClose={() => setOpenProductDialog(false)}
        onProductAdded={(newProduct) => {
          setPendingProductForTable({
            value: newProduct.Id,
            label: newProduct.Name,
            ...newProduct,
          });
          setOpenProductDialog(false);
        }}
        initialName={productSearchText}
      />
      <UnsavedChangesDialog
        open={blocker.state === 'blocked'}
        onDiscard={() => blocker.proceed?.()}
        onCancel={() => blocker.reset?.()}
      />
      <InvoicePreviewDialog
        open={openPreview}
        onClose={() => setOpenPreview(false)}
        html={previewHtml}
        documentTitle={`offer-${offerNumber || "draft"}`}
      />
    </Box>
  );
}

export default AddOfferPage;
